import { Box, Flex, SystemProps } from "flicket-ui";
import { useContext } from "react";
import { SocialButtonCard } from "~components/common/Social/SocialButtonCard";
import { CompetitionContext } from "~features/competitions/CompetitionContext";
import {
  PromoterUserAction,
  SocialUserInteractions,
} from "~features/competitions/helpers";
import { CompetitionType } from "~graphql/sdk";
import { useOrganization } from "~hooks";
import { useSocialShare } from "~hooks/useSocialShare";
import { openExternalLink } from "~lib/helpers/openExternalLink";
import { useReferralLink } from "../hooks/useReferralLink";
import { useSocialLinkOptions } from "../hooks/useSocialLinkOptions";
import { pick } from "@styled-system/props";

export interface ShareOptionsProps extends SystemProps {
  eventId: string;
  eventTitle: string;
  eventPhase: CompetitionType;
  waitlistUserId?: string;
  orderId?: string;
}

export type SocialLink = {
  icon: string;
  label: string;
  handler: () => void;
  action: PromoterUserAction;
  numEntries?: number;
  analyticsEventName: SocialUserInteractions;
};

export const PromoterShareCard = ({
  eventId,
  eventTitle,
  eventPhase,
  waitlistUserId,
  orderId,
  ...props
}: ShareOptionsProps) => {
  const { organization } = useOrganization();
  const competitionContext = useContext(CompetitionContext);

  const socialOptions = useSocialLinkOptions({
    eventId,
    activeCompetition: competitionContext?.competition,
  });

  const findSocialUrl = (promoterAction: PromoterUserAction) => {
    const socialUrl = socialOptions.find(
      (s) => s.promoterAction === promoterAction
    )?.socialUrl;

    return socialUrl;
  };

  const findEntries = (promoterAction: PromoterUserAction) =>
    socialOptions?.find((s) => s.promoterAction === promoterAction)?.entries;

  const link = useReferralLink({
    eventId,
    waitlistUserId,
    orderId,
    organizationSlug: organization.slug,
  });

  const { facebookHandler, twitterHandler, emailHandler, copyHandler } =
    useSocialShare();

  const socialLinks: SocialLink[] = [
    {
      label: "Post on Facebook",
      icon: "facebook",
      handler: () =>
        facebookHandler({
          url: link,
          variant: "share",
        }),
      action: "facebook:share",
      numEntries: findEntries("facebook:share"),
      analyticsEventName: "share-on-facebook",
    },
    {
      label: "Share on X",
      icon: "x",
      handler: () =>
        twitterHandler({
          url: link,
          eventTitle,
          variant: "tweet",
        }),
      action: "twitter:share",
      numEntries: findEntries("twitter:share"),
      analyticsEventName: "share-on-twitter",
    },
    {
      label: "Copy link",
      icon: "copy-link",
      handler: () => copyHandler({ url: link }),
      action: "copy:link",
      numEntries: findEntries("copy:link"),
      analyticsEventName: "copy-link",
    },
    {
      label: "Invite via Messenger",
      icon: "messenger",
      handler: () =>
        facebookHandler({
          url: link,
          variant: "direct-message",
        }),
      numEntries: findEntries("messenger:invite"),
      analyticsEventName: "invite-on-messenger",
      action: "messenger:invite",
    },
    {
      label: "Invite via X",
      icon: "x",
      handler: () =>
        twitterHandler({ url: link, eventTitle, variant: "direct-message" }),
      action: "twitter:invite",
      numEntries: findEntries("twitter:invite"),
      analyticsEventName: "invite-on-twitter",
    },
    {
      label: "Like on Facebook",
      icon: "fb-like",
      handler: () => openExternalLink({ url: findSocialUrl("facebook:like") }),
      analyticsEventName: "like-on-facebook",
      numEntries: findEntries("facebook:like"),
      action: "facebook:like",
    },
    {
      label: "Facebook RSVP",
      icon: "facebook-event",
      handler: () => openExternalLink({ url: findSocialUrl("facebook:rsvp") }),
      analyticsEventName: "rsvp-facebook",
      numEntries: findEntries("facebook:rsvp"),
      action: "facebook:rsvp",
    },
    {
      label: "Follow on Instagram",
      icon: "instagram",
      handler: () =>
        openExternalLink({ url: findSocialUrl("instagram:follow") }),
      analyticsEventName: "follow-on-instagram",
      numEntries: findEntries("instagram:follow"),
      action: "instagram:follow",
    },
    {
      label: "Follow on Tik Tok",
      icon: "tiktok",
      handler: () => openExternalLink({ url: findSocialUrl("tiktok:follow") }),
      analyticsEventName: "follow-on-tiktok",
      numEntries: findEntries("tiktok:follow"),
      action: "tiktok:follow",
    },
    {
      label: "Share via Email",
      icon: "email",
      handler: () =>
        emailHandler({
          url: link,
          eventTitle,
        }),
      action: "email:invite",
      numEntries: findEntries("email:invite"),
      analyticsEventName: "invite-via-email",
    },
  ];

  const allActions = socialOptions.map((s) => s.promoterAction);
  // Always add the copy link action as the backend doesn't send this.
  allActions.push("copy:link");

  return (
    <Flex flexWrap="wrap" width="100%" {...pick(props)}>
      {socialLinks
        // Show highest numEntries first
        .sort((a, b) => (b.numEntries ?? 0) - (a.numEntries ?? 0))
        .filter((socialLink) => allActions.includes(socialLink.action))
        .map((socialLink) => (
          <Box
            key={socialLink.label}
            width={{ base: "100%", md: "50%" }}
            p={"1/2"}
          >
            <SocialButtonCard
              socialLink={socialLink}
              eventId={eventId}
              eventPhase={eventPhase}
            />
          </Box>
        ))}
    </Flex>
  );
};
